import React, { useState } from 'react';
import { Box, Typography, Button, Tooltip, IconButton, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ZapIcon from '@mui/icons-material/FlashOn';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import TvIcon from '@mui/icons-material/Tv';
import POS from '@/components/pos';

interface SidebarFooterProps {
    open: boolean;
}

export const SidebarFooter: React.FC<SidebarFooterProps> = ({ open }) => {
    const theme = useTheme();
    const [isPosOpen, setIsPosOpen] = useState(false);

    return (
        <Box sx={{ p: open ? 2 : 1, mt: 'auto', width: '100%', flexShrink: 0 }} id="sidebar-footer">
            <Box
                sx={{
                    position: 'relative',
                    overflow: 'hidden',
                    p: open ? 2 : 0,
                    borderRadius: open ? '16px' : '0px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: open 
                        ? (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.6)')
                        : 'transparent',
                    backdropFilter: open ? 'blur(12px)' : 'none',
                    border: open
                        ? (theme.palette.mode === 'dark'
                            ? '1px solid rgba(255, 255, 255, 0.1)'
                            : '1px solid rgba(255, 255, 255, 0.5)')
                        : 'none',
                    boxShadow: open ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                    transition: 'all 0.3s ease',
                    '&:hover .decorative-circle': {
                        transform: 'scale(1.5)',
                    },
                }}
            >
                {open && (
                    <Box
                        className="decorative-circle"
                        sx={{
                            position: 'absolute',
                            top: -16,
                            right: -16,
                            width: 64,
                            height: 64,
                            bgcolor: theme.palette.mode === 'dark' ? '#541370' : '#e1bee7',
                            borderRadius: '50%',
                            opacity: 0.5,
                            filter: 'blur(20px)',
                            transition: 'transform 0.7s ease',
                            zIndex: 0,
                        }}
                    />
                )}

                {open ? (
                    <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, position: 'relative', zIndex: 1, width: '100%' }}>
                            <Box sx={{ p: 1, display: 'flex', background: 'linear-gradient(135deg, #f3e5f5 0%, #ffffff 100%)', color: '#7b1fa2', borderRadius: '12px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
                                <ZapIcon sx={{ fontSize: 18 }} />
                            </Box>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.2, fontSize: '0.875rem' }}>
                                    POS Terminal
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '10px' }}>
                                    Quick Sales Mode
                                </Typography>
                            </Box>
                        </Box>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => setIsPosOpen(true)}
                            startIcon={<CreditCardIcon />}
                            sx={{
                                position: 'relative',
                                zIndex: 1,
                                background: 'linear-gradient(135deg, #8b2aa0 0%, #611e70 100%)',
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '12px',
                                color: 'white',
                                py: 1.2,
                                boxShadow: '0 8px 16px rgba(139, 42, 160, 0.25)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #7a258c 0%, #51185d 100%)',
                                    boxShadow: '0 12px 24px rgba(139, 42, 160, 0.35)',
                                    transform: 'translateY(-2px)',
                                },
                                '&:active': {
                                    transform: 'translateY(1px)',
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            POS Нээх
                        </Button>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={() => window.open('/xmedia-emulator', '_blank')}
                            startIcon={<TvIcon />}
                            sx={{
                                position: 'relative',
                                zIndex: 1,
                                background: 'linear-gradient(135deg, #e50914 0%, #b20710 100%)',
                                textTransform: 'none',
                                fontWeight: 700,
                                borderRadius: '12px',
                                color: 'white',
                                py: 1.2,
                                mt: 1,
                                boxShadow: '0 8px 16px rgba(229, 9, 20, 0.25)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #f40a16 0%, #99050d 100%)',
                                    boxShadow: '0 12px 24px rgba(229, 9, 20, 0.35)',
                                    transform: 'translateY(-2px)',
                                },
                                '&:active': {
                                    transform: 'translateY(1px)',
                                },
                                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            }}
                        >
                            STB Emulator
                        </Button>
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                        <Tooltip title="POS Нээх" placement="right">
                            <IconButton
                                onClick={() => setIsPosOpen(true)}
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    color: theme.palette.mode === 'dark' ? '#e1bee7' : theme.palette.primary.main,
                                    width: 44,
                                    height: 44,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? alpha('#e1bee7', 0.1) : alpha(theme.palette.primary.main, 0.1),
                                    },
                                }}
                            >
                                <CreditCardIcon sx={{ fontSize: 24 }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="STB Emulator" placement="right">
                            <IconButton
                                onClick={() => window.open('/xmedia-emulator', '_blank')}
                                sx={{
                                    position: 'relative',
                                    zIndex: 1,
                                    color: theme.palette.mode === 'dark' ? '#ff8a80' : '#e50914',
                                    width: 44,
                                    height: 44,
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                        bgcolor: theme.palette.mode === 'dark' ? alpha('#ff8a80', 0.1) : alpha('#e50914', 0.1),
                                    },
                                }}
                            >
                                <TvIcon sx={{ fontSize: 24 }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                )}

                {isPosOpen && <POS open={isPosOpen} onClose={() => setIsPosOpen(false)} />}
            </Box>
        </Box>
    );
};
